import React, { useEffect, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useAdminAuth } from './useAdminAuth';
import { AdminTokenProtectedRoute } from '../../shared/components';
import AdminLoginScreen from './AdminLoginScreen';
import AdminDashboard from '../admin/AdminDashboard';
import UserManagement from '../admin/users/UserManagement';
import { EventManagement, EventDetails } from '../admin/events';
import { CashMovementsManagement } from '../admin/cash-movements';
import ConfigManagement from '../admin/config/ConfigManagement';
import ReservationManagement from '../admin/reservations/ReservationManagement';
import { TicketSalesDashboard } from '../ticket-sales';
import { StudentsManagement } from '../admin/students';
import { AuditLogsManagement } from '../admin/audit-logs';
import { NotificationsManagement } from '../admin/notifications';

const AdminRoute = () => {
  const { isAuthenticated, adminData, loading, error, login, logout, clearError } = useAdminAuth();
  const navigate = useNavigate();
  const previousAuthState = useRef(isAuthenticated);

  // Redirigir a /admin después del login exitoso
  useEffect(() => {
    // Solo redirigir si el usuario acaba de autenticarse (cambio de false a true)
    if (isAuthenticated && !previousAuthState.current) {
      navigate('/admin', { replace: true });
    }
    // Actualizar el estado anterior
    previousAuthState.current = isAuthenticated;
  }, [isAuthenticated, navigate]);

  // Limpiar ruta a /admin cuando el usuario se desautentica
  useEffect(() => {
    if (!isAuthenticated && previousAuthState.current) {
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, navigate]);



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Permitir acceso a /admin/ticket-sales con token de access code aunque no haya admin
  const isTicketSalesPath = window.location.pathname.startsWith('/admin/ticket-sales');
  const theaterToken = localStorage.getItem('theaterAccessCodeToken');
  if (!isAuthenticated && isTicketSalesPath && theaterToken) {
    return (
      <Routes>
        <Route 
          path="/ticket-sales" 
          element={<TicketSalesDashboard adminData={null} onLogout={logout}  />} 
        />
      </Routes>
    );
  }

  if (!isAuthenticated) {
    return <AdminLoginScreen onLogin={login} error={error} onClearError={clearError} />;
  }

  return (
    <AdminTokenProtectedRoute>
      <Routes>
        <Route 
          path="/" 
          element={
            <AdminDashboard 
              adminData={adminData} 
              onLogout={logout} 
            />
          } 
        />
        <Route 
          path="/users" 
          element={
            <UserManagement 
              adminData={adminData} 
              onLogout={logout} 
            />
          } 
        />

        <Route 
          path="/students" 
          element={
            <StudentsManagement 
              adminData={adminData} 
              onLogout={logout} 
            />
          } 
        />

        <Route 
          path="/events" 
          element={
            <EventManagement 
              adminData={adminData} 
              onLogout={logout} 
            />
          } 
        />
        <Route 
          path="/events/:eventId/details" 
          element={
            <EventDetails 
              adminData={adminData} 
              onLogout={logout} 
            />
          } 
        />
        <Route 
          path="/config" 
          element={
            <ConfigManagement 
              adminData={adminData} 
              onLogout={logout} 
            />
          } 
        />
        <Route 
          path="/reservations" 
          element={
            <ReservationManagement 
              adminData={adminData} 
              onLogout={logout} 
            />
          } 
        />
        <Route 
          path="/ticket-sales" 
          element={
            <TicketSalesDashboard 
              adminData={adminData} 
              onLogout={logout} 
            />
          } 
        />
        <Route 
          path="/audit-logs" 
          element={
            <AuditLogsManagement 
              adminData={adminData} 
              onLogout={logout} 
            />
          } 
        />
        <Route 
          path="/notifications" 
          element={
            <NotificationsManagement 
              adminData={adminData} 
              onLogout={logout} 
            />
          } 
        />
        <Route 
          path="/cash-movements" 
          element={
            <CashMovementsManagement 
              adminData={adminData} 
              onLogout={logout} 
            />
          } 
        />
      </Routes>
    </AdminTokenProtectedRoute>
  );
};

export default AdminRoute; 